// Resilient fetch with exponential backoff + jitter.
//
// Cloud Run services in this project scale to zero, so the first request after
// an idle period can hit a cold start and return a transient 502/503/504, time
// out, or fail at the connection level before the app code ever runs. Those are
// infrastructure hiccups, not real errors — the UI must never surface them. This
// wrapper transparently retries such failures with a growing delay (the "growth
// window") capped by a max delay and a max attempt count (the "cutoff"), so a
// single cold start self-heals without the user seeing an error or refreshing.

export interface RetryOptions {
  /** Total attempts including the first try. */
  maxAttempts?: number;
  /** Base delay for the first backoff step, in ms. */
  baseDelayMs?: number;
  /** Upper bound for any single backoff delay, in ms (the growth cutoff). */
  maxDelayMs?: number;
  /** Multiplier applied to the delay after each failed attempt. */
  factor?: number;
  /** Abort + retry a single attempt if it exceeds this many ms (cold-start hang guard). */
  perAttemptTimeoutMs?: number;
}

const DEFAULTS: Required<RetryOptions> = {
  maxAttempts: 4,
  baseDelayMs: 400,
  maxDelayMs: 8_000,
  factor: 2,
  perAttemptTimeoutMs: 20_000,
};

// Statuses that indicate the request was not meaningfully processed and is safe
// to retry for ANY HTTP method (pure infrastructure / overload signals).
const INFRA_RETRY_STATUSES = new Set([408, 425, 429, 502, 503, 504]);
// 500 may mean a mutation partially applied, so it is only retried for
// idempotent methods where a repeat is harmless.
const IDEMPOTENT_RETRY_STATUSES = new Set([500]);
const IDEMPOTENT_METHODS = new Set(["GET", "HEAD", "OPTIONS"]);

function isRetryableStatus(status: number, method: string): boolean {
  if (INFRA_RETRY_STATUSES.has(status)) return true;
  if (IDEMPOTENT_METHODS.has(method) && IDEMPOTENT_RETRY_STATUSES.has(status)) return true;
  return false;
}

function sleep(ms: number): Promise<void> {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

// Equal-jitter backoff: half fixed exponential growth, half random, capped at
// maxDelayMs. Honors a server-provided Retry-After when present.
function backoffDelay(attempt: number, opts: Required<RetryOptions>, retryAfterMs?: number): number {
  if (retryAfterMs && retryAfterMs > 0) {
    return Math.min(retryAfterMs, opts.maxDelayMs);
  }
  const exp = Math.min(opts.maxDelayMs, opts.baseDelayMs * Math.pow(opts.factor, attempt - 1));
  return exp / 2 + Math.random() * (exp / 2);
}

function parseRetryAfter(res: Response): number | undefined {
  const header = res.headers.get("Retry-After");
  if (!header) return undefined;
  const seconds = Number(header);
  if (Number.isFinite(seconds)) return seconds * 1000;
  const date = Date.parse(header);
  if (Number.isFinite(date)) return Math.max(0, date - Date.now());
  return undefined;
}

/**
 * fetch() with automatic retry of transient failures. The returned Response is
 * either a success or a final, non-retryable response; the caller still handles
 * application-level status codes (e.g. 401/403/422) as before.
 *
 * The caller's own AbortSignal (init.signal) is always respected and never
 * retried — only our per-attempt timeout and transient transport errors are.
 */
export async function retryingFetch(
  input: string,
  init: RequestInit = {},
  options: RetryOptions = {},
): Promise<Response> {
  const opts = { ...DEFAULTS, ...options };
  const method = (init.method ?? "GET").toUpperCase();
  const callerSignal = init.signal ?? undefined;

  let lastError: unknown;

  for (let attempt = 1; attempt <= opts.maxAttempts; attempt++) {
    if (callerSignal?.aborted) throw new DOMException("Aborted", "AbortError");

    const timeout = new AbortController();
    const timer = setTimeout(() => timeout.abort(), opts.perAttemptTimeoutMs);
    // Abort this attempt if either the caller cancels or our timeout fires.
    const onCallerAbort = () => timeout.abort();
    callerSignal?.addEventListener("abort", onCallerAbort, { once: true });

    try {
      const res = await fetch(input, { ...init, signal: timeout.signal });

      if (attempt < opts.maxAttempts && isRetryableStatus(res.status, method)) {
        const delay = backoffDelay(attempt, opts, parseRetryAfter(res));
        await sleep(delay);
        continue;
      }
      return res;
    } catch (err) {
      lastError = err;
      // A cancellation requested by the caller (not our timeout) is final.
      if (callerSignal?.aborted) throw err;
      // Network error or per-attempt timeout — retry if attempts remain.
      if (attempt < opts.maxAttempts) {
        await sleep(backoffDelay(attempt, opts));
        continue;
      }
      throw err;
    } finally {
      clearTimeout(timer);
      callerSignal?.removeEventListener("abort", onCallerAbort);
    }
  }

  // Unreachable in practice (loop either returns or throws), but satisfies types.
  throw lastError ?? new Error("retryingFetch: exhausted attempts");
}
